<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import AssessmentChart from '../AssessmentChart/AssessmentChart.vue'

const props = defineProps({
  review: {
    type: Object,
    default: undefined,
  },
  implementedActions: {
    type: Array,
    default: () => [],
  },
})

const reviewOptions = [
  { value: 'quickWins', label: 'Hebel für schnelle Inklusionsgewinne' },
  { value: 'majorProjects', label: 'Aufwändige Schlüsselmaßnahmen' },
  { value: 'fillers', label: 'Unterstützende kleine Aktivitäten' },
  {
    value: 'avoid',
    label: 'Wirkungssschwache Ressourcenfresser',
  },
]

const labelMap = Object.fromEntries(
  reviewOptions.map((o) => [o.value, o.label]),
)

// --- Highlight state for chart ↔ table interaction ---
const highlightedId = ref(null)

const onRowHover = (id) => {
  if (window.innerWidth < 1024) return
  highlightedId.value = id
}

const onRowClick = (id) => {
  if (highlightedId.value == id) {
    highlightedId.value = null
    return
  }

  highlightedId.value = id
}

const onRowLeave = () => {
  if (window.innerWidth < 1024) return
  highlightedId.value = null
}

const onPointClick = (id) => {
  const row = document.getElementById(`action-row-${id}`)
  if (row) {
    if (highlightedId.value == id) {
      highlightedId.value = null
      return
    }

    row.scrollIntoView({ behavior: 'smooth', block: 'center' })
    highlightedId.value = id
  }
}

// --- CSV export ---
const escapeCsvField = (value) => {
  const str = String(value ?? '')
  return str.includes(',') || str.includes('"') || str.includes('\n')
    ? `"${str.replace(/"/g, '""')}"`
    : str
}

const exportCsv = () => {
  const rows = [['Maßnahme', 'Impact', 'Aufwand', 'Score']]

  for (const key of reviewOptions.map((o) => o.value)) {
    const quadrant = props.review.quadrants[key]
    if (!quadrant?.length) continue
    for (const action of quadrant) {
      const title = action.customDefinition || action.title
      const score = (6 - action.effort) * action.impact
      rows.push([escapeCsvField(title), action.impact, action.effort, score])
    }
  }

  const csv = rows.map((r) => r.join(',')).join('\n')
  const blob = new Blob(['\uFEFF' + csv], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'auswertung.csv'
  link.click()
  URL.revokeObjectURL(url)
}

const chartRef = ref(null)
const pipImageUrl = ref(null)
let pipObserver = null

onMounted(() => {
  pipObserver = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting && window.innerWidth < 1000) {
        const canvas = chartRef.value?.querySelector('canvas')
        pipImageUrl.value = canvas?.toDataURL() ?? null
      } else {
        pipImageUrl.value = null
      }
    },
    { threshold: 0 },
  )
  if (chartRef.value) pipObserver.observe(chartRef.value)
})

onBeforeUnmount(() => pipObserver?.disconnect())

const refreshPip = async () => {
  if (!pipImageUrl.value) return
  await nextTick()
  const canvas = chartRef.value?.querySelector('canvas')
  if (canvas) pipImageUrl.value = canvas.toDataURL()
}

watch(highlightedId, refreshPip)

const exportImplemented = () => {
  const rows = [['Maßnahme']]
  for (const action of props.implementedActions) {
    rows.push([escapeCsvField(action.title)])
  }
  const csv = rows.map((r) => r.join(',')).join('\n')
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'bereits-umgesetzt.csv'
  link.click()
  URL.revokeObjectURL(url)
}

const emit = defineEmits(['back'])
</script>

<template>
  <div class="AssessmentReview">
    <div ref="chartRef" class="AssessmentReview-chart">
      <AssessmentChart
        :review="props.review"
        :highlighted-id="highlightedId"
        @point-click="onPointClick"
        @chart-click="highlightedId = null"
      />
    </div>

    <div
      v-if="pipImageUrl"
      class="AssessmentReview-pip"
      aria-hidden="true"
      @click.prevent="chartRef.scrollIntoView({ behavior: 'smooth' })"
    >
      <img aria-hidden="true" :src="pipImageUrl" alt="" />
    </div>

    <div class="AssessmentReview-main">
      <div class="AssessmentReview-headline">Auswertung</div>

      <div v-if="review.areas?.length" class="AssessmentReview-legend">
        <span
          v-for="area in review.areas"
          :key="area.color"
          class="AssessmentReview-legendItem"
        >
          <span
            class="AssessmentReview-legendDot"
            :style="{ backgroundColor: area.color }"
          />
          {{ area.title }}
        </span>
      </div>

      <ul class="AssessmentReview-quadrants">
        <li
          v-for="(quadrant, index) in props.review.quadrants"
          :key="index"
          class="AssessmentReview-quadrant"
        >
          <details class="AssessmentReview-quadrantDetails" open>
            <summary class="AssessmentReview-quadrantHeadline">
              {{ labelMap[index] }}
              <svg
                class="AssessmentReview-quadrantHeadlineIcon"
                aria-hidden="true"
              >
                <use href="#arrow-down"></use>
              </svg>
            </summary>
            <table
              v-if="quadrant.length > 0"
              class="AssessmentReview-quadrantTable"
            >
              <thead>
                <tr class="AssessmentReview-quadrantTableRow">
                  <th
                    class="AssessmentReview-quadrantTableData AssessmentReview-quadrantTableData--title"
                  >
                    Maßnahme
                  </th>
                  <th
                    class="AssessmentReview-quadrantTableData AssessmentReview-quadrantTableData--impact"
                  >
                    Impact
                  </th>
                  <th
                    class="AssessmentReview-quadrantTableData AssessmentReview-quadrantTableData--effort"
                  >
                    Aufwand
                  </th>
                  <!-- <th
                  class="AssessmentReview-quadrantTableData AssessmentReview-quadrantTableData--score"
                >
                  Score
                </th> -->
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="action in quadrant"
                  :id="`action-row-${action.id}`"
                  :key="action.id"
                  class="AssessmentReview-quadrantTableRow"
                  :class="{
                    'AssessmentReview-quadrantTableRow--highlighted':
                      action.id == highlightedId,
                  }"
                  :style="
                    action.areaColor ? { '--area-color': action.areaColor } : {}
                  "
                  @mouseenter="onRowHover(action.id)"
                  @click="onRowClick(action.id)"
                  @mouseleave="onRowLeave"
                >
                  <td
                    class="AssessmentReview-quadrantTableData AssessmentReview-quadrantTableData--title"
                    data-name="Maßnahme"
                  >
                    <b>{{ action.key }}&nbsp;</b
                    >{{
                      action.customDefinition
                        ? action.customDefinition
                        : action.title
                    }}
                  </td>
                  <td
                    class="AssessmentReview-quadrantTableData AssessmentReview-quadrantTableData--impact"
                    data-name="Impact:"
                  >
                    {{ action.impact }}
                  </td>
                  <td
                    class="AssessmentReview-quadrantTableData AssessmentReview-quadrantTableData--effort"
                    data-name="Aufwand:"
                  >
                    {{ action.effort }}
                  </td>
                  <!-- <td
                  class="AssessmentReview-quadrantTableData AssessmentReview-quadrantTableData--score"
                >
                  {{ (6 - action.effort) * action.impact }}
                </td> -->
                </tr>
              </tbody>
            </table>
            <p
              v-if="quadrant.length == 0"
              class="AssessmentReview-quadrantHint"
            >
              Keine Maßnahmen in dieser Kategorie vorhanden.
            </p>
          </details>
        </li>
      </ul>

      <div class="AssessmentReview-export">
        <button
          class="AssessmentReview-csvExport button button--primary"
          @click="exportCsv"
        >
          <svg class="icon" aria-hidden="true">
            <use href="#download"></use>
          </svg>
          <span>Auswertung herunterladen (.csv)</span>
        </button>
        <button
          class="AssessmentReview-csvExport button button--primary"
          :disabled="implementedActions.length === 0"
          @click="exportImplemented"
        >
          <svg class="icon" aria-hidden="true">
            <use href="#download"></use>
          </svg>
          <span>Bereits umgesetzte Maßnahmen herunterladen (.csv)</span>
        </button>
      </div>

      <div class="AssessmentReview-buttons">
        <button class="button" @click="emit('back')">
          <svg class="icon" aria-hidden="true">
            <use href="#arrow-left"></use>
          </svg>
          <span>zurück zur Bewertung</span>
        </button>
      </div>
    </div>
  </div>
</template>
